"use client";
import { EnrichedProgram, ProcessedCourse } from "@/common/course/types";
import { Program } from "@/common/program/types";
import { ProductViewTracker } from "@/components/ProductViewTracker";
import { useCourseCheckoutUrl } from "@/hooks/useCourseCheckoutUrl";
import { useCompareCtaUrl } from "@/components/ProductCompare/useCompareCtaUrl";
import { useSingleCourseTrafficConfig } from "@/monarch/rules/singleCourseTraffic/useSingleCourseTraffic";
import useLeadWithPaid from "@/monarch/rules/leadWithPaid/useLeadWithPaid";
import {
  mainContent as mainContentClass,
  maxWidthContainer,
} from "@/components/CourseContent/styles/constants";
import getSortedComponentsBySectionOrder from "@/utils/getSortedComponentsBySectionOrder";
import PartnershipDescription from "@/components/ProgramDetailLayout/templates/components/PartnershipDescription";
import { PartialPartner } from "@/common/partner/types";
import { Instructor } from "@/common/instructor/types";

import ISO6391 from "iso-639-1";
import { getAiTranslationLanguageLabels } from "@/lib/course/utils/getAiLanguages";
import CourseTaggedHeader from "@/components/CourseContent/templates/components/CourseTaggedHeader";
import AlumniTestimonials from "@/components/AlumniTestimonials";
import Highlights from "@/components/Highlights";
import CourseEnrollButton from "@/components/CourseContent/ContentRefresh/CourseEnroll/CourseEnrollButtons";
import EnrollLink from "@/components/CourseContent/ContentRefresh/CourseEnroll/CourseEnrollButtons/EnrollLink";
import AuditLink from "@/components/CourseContent/ContentRefresh/CourseEnroll/CourseEnrollButtons/AuditLink";
import CourseAbout from "@/components/CourseContent/ContentOverview";
import MidPageCTA from "@/components/CourseContent/ContentRefresh/MidPageCTA";
import WhatYoullLearn from "@/components/CourseContent/ContentRefresh/WhatYoullLearn";
import InstructorList from "@/components/InstructorList";
import FAQs from "@/components/CourseContent/ContentRefresh/FAQs";
import WaysToTakeCourse from "@/components/CourseContent/ContentRefresh/WaysToTakeCourse";
import CertificateWaysToTakeCourse from "@/components/Certificate/CertificateWaysToTakeCourse";
import Curriculum from "@/components/CourseContent/Curriculum";
import CountdownBanner from "@/components/CountdownBanner";
import { FeatureFlagComponentResolverClient } from "@/lib/feature-flags/FeatureFlagResolverClient";
import { CourseCurrencyBarSurface } from "@/components/CourseContent/templates/components/CourseCurrencySurfaces";
import SkillLanguageOverview from "@/components/CourseContent/ContentRefresh/SkillLanguageOverview";
import CertificateEmphasis from "@/components/Certificate/CertificateEmpashis";
import CertificateEmphasisTabs from "@/components/Certificate/CertificateEmphasisTabs";
import { RedesignedTrackComparison } from "@/components/Certificate/constants/enums";
import LearnerTestimonials from "@/components/CourseContent/LearnerTestimonials";
import StepComponent from "@/components/StepComponent";
import VerificationUpsell from "@/components/CourseContent/VerificationUpsell";
import AccordionText from "@/components/AccordionTextRefresh";
import AccordionTextItem from "@/components/AccordionTextRefresh/AccordionTextItemRefresh";
import { useCourseDetailPricingData } from "@/lib/checkout-geo-pricing";
import isCourseArchived from "@/lib/course/utils/isCourseArchived";
import RelatedProgram from "../common/utils/renderRelatedProgram";
import { useEffect } from "react";
import SocialShare from "@/components/SocialShare";
import EnterpriseCTA from "../../ContentRefresh/EnterpriseCTA";
import OFACMessage from "@/components/CourseContent/ContentRefresh/OFACmessage";
import CourseAwards from "@/components/CourseContent/templates/components/Awards";
import LanguageOverview from "../../ContentRefresh/LanguageOverview";
import CourseDetails from "../../CourseDetails";
import Stats from "@/components/Stats";
import OCPromoBanner from "../../ContentRefresh/OCPromoBanner";
import MoreAboutCourse from "@/components/CourseContent/ContentRefresh/MoreAboutCourse";
import useClientEnrollments from "@/lib/partner-api/useClientEnrollments";
import useClientEntitlements from "@/lib/partner-api/useClientEntitlements";
import useUserEnrollments from "../../CourseContext/hooks/useUserEnrollments";
import useUserEntitlements from "../../CourseContext/hooks/useUserEntitlements";
import useCourseDetailsData from "../../CourseContext/hooks/useCourseDetailsData";
import isUserEnrolled from "@/lib/course/utils/isUserEnrolled";
import { getPartnersDisplayNames } from "@/lib/partner/utils/getPartnerDisplayName";
import BenefitsModule from "@/components/BenefitsModule";
import PartnerInstructors from "@/components/PartnerInstructors";
import BundleSavingsBanner from "@/components/BundleSavingsBanner/BundleSavingsBanner";
import ProductCompare from "@/components/ProductCompare";
import useCompareFallbackUuids from "@/components/ProductCompare/useCompareFallbackUuids";
import { getProgramSearchUrl } from "../common/utils/getProgramSearchUrl";
import CareerCards from "@/components/CareerCards";
import {
  CAREER_CARDS_VARIANTS,
  resolveCareerCardSkills,
  getIndustryKey,
} from "@/components/CareerCards/constants";
import { courseDetailPageEventConfig } from "../common/const/const";
import SocialProof from "@/components/SocialProof";
import { headerStylesInDetailPages } from "@/components/SocialProof/const";
import StickyCTA from "@/components/CourseContent/ContentRefresh/StickyCTA";
import WhyLearnersChoose, {
  HighlightItem,
} from "@/components/WhyLearnersChoose/index";
import { DEFAULT_HIGHLIGHTS_ITEMS } from "@/components/Highlights/constants";
import useTranslations from "@/i18n/useTranslations";

type Props = {
  course: ProcessedCourse;
  program?: Program | null;
  programPacingType?: string;
  programDuration?: string;
  hoursPerWeekFormatted?: string;
  paymentOption: "free" | "upgradable" | "paid" | null;
  showCourseModes: boolean;
  category?: string;
  courseSlug?: string;
  enrichedPrograms?: EnrichedProgram[] | null;
  enrichedInstructors?: Instructor[];
};

export default function ReferralCourseContent({
  course,
  showCourseModes,
  enrichedPrograms,
  paymentOption,
  category,
  courseSlug,
  enrichedInstructors,
}: Props) {
  const tCourse = useTranslations("Course");
  const tProgram = useTranslations("Program");
  const {
    mainContent,
    partnershipDescription,
    headerBackground,
    headerPricePosition,
    headerPriceLabel,
    simpleMobileHeader,
    alumniTestimonials,
    highlights,
    promoBanner,
    carouselData,
    relatedProgramsRedesign: {
      design2: expandableSection,
      designToDisplay,
      cardType,
    } = {},
    statsBanner,
    showRedesignedTrackComparison,
    tableDescription,
    hidePrice,
    countdownBanner,
    emphasisModuleVersion,
    information,
    stats,
    testimonial,
    tabsOrder,
    pathInfo,
    stepComponent,
    verificationUpsellBanner,
    benefitsModule,
    testimonialsModule,
    bundleSavingsBanner,
    productCompare,
    showDynamicBadges,
    careerCards,
  } = useSingleCourseTrafficConfig({ course });

  const compareFallbackUuids = useCompareFallbackUuids({
    source: productCompare?.relatedProductsSource,
    relatedProductsFilters: productCompare?.relatedProductsFilters,
    enrichedPrograms,
    course,
  });

  const titlePartner = getPartnersDisplayNames(course.partners);

  //fetch enrollment data
  const { enrollments: userEnrollments } = useClientEnrollments();

  //fetch entitlement data
  const { entitlements: userEntitlements } = useClientEntitlements();

  const { setIsUserEnrolled, setUserEnrollments } = useUserEnrollments();
  const { setUserEntitled } = useUserEntitlements();

  const { setCourseDetailsData } = useCourseDetailsData();

  // Generate checkout URL with SKU and course run key
  const checkoutUrl = useCourseCheckoutUrl(course);
  const compareCtaUrl = useCompareCtaUrl(course);

  const leadWithPaidData = useLeadWithPaid(course);

  const {
    languageId,
    transcriptLanguages,
    weeksToComplete,
    minEffort,
    maxEffort,
  } = course.activeCourseRun || {};

  const isArchived = course.activeCourseRun
    ? isCourseArchived(course.activeCourseRun)
    : true;

  const { productFullDescription, video, productImageUrl, syllabusHtml } =
    course;

  const isCurriculumInMainContent = mainContent?.some(
    (component) => component.component === "curriculum",
  );

  const courseHeaderVideo = {
    image: {
      src: productImageUrl,
    },
    src: video.src,
  };

  const formattedLanguage = languageId
    ? ISO6391.getNativeName(languageId.slice(0, 2))
    : undefined;
  const formattedTranscripts =
    transcriptLanguages?.map((transcript) =>
      ISO6391.getNativeName(transcript.slice(0, 2)),
    ) || [];

  const formattedAiLanguages = course.activeCourseRun
    ? getAiTranslationLanguageLabels(course.activeCourseRun)
    : [];

  const pricingData = useCourseDetailPricingData({ course });
  const coursePrice =
    pricingData?.discountedPrice ?? pricingData?.originalPrice ?? null;

  useEffect(() => {
    setCourseDetailsData({
      category: category,
      courseSlug: courseSlug,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, courseSlug]);

  useEffect(() => {
    if (userEntitlements && userEntitlements.length > 0) {
      const found =
        userEntitlements?.some(
          ({ courseUuid: userEntitledCourseUuid }) =>
            userEntitledCourseUuid === course.activeCourseRun?.uuid,
        ) || false;
      setUserEntitled(found);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userEntitlements]);

  //set context enrollmment status
  useEffect(() => {
    if (userEnrollments && userEnrollments.length > 0) {
      setUserEnrollments(userEnrollments);
      const courseIds = course.activeCourseRuns?.map(({ key }) => key) || [];
      const isEnrolled = isUserEnrolled(userEnrollments, courseIds);
      setIsUserEnrolled(isEnrolled);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userEnrollments]);

  const componentList = [
    {
      key: "courseAwards",
      renderer: () => {
        return course.customizations.catalogPage?.awards?.length ? (
          <CourseAwards
            title={tCourse("awardWinningCourse")}
            awardsContainerClassName="flex flex-row gap-6 p-10 pr-6 bg-putty-200 border border-putty-400 rounded-xl"
            width={103}
            height={103}
            key="courseAwards"
            awards={course.customizations.catalogPage.awards}
            isSpanish={false}
          />
        ) : null;
      },
    },
    {
      key: "whatYoullLearn",
      renderer: () => {
        return (
          <div className="hidden md:block [&_ul]:!px-0 md:[&_ul]:px-4">
            <WhatYoullLearn
              key="whatYoullLearn"
              title={tCourse("whatYoullLearn")}
              parseContent={course.productOverview}
            />
          </div>
        );
      },
    },
    {
      key: "skillLanguageOverview",
      renderer: () => {
        return course.associatedSkills?.length > 0 ||
          !syllabusHtml ||
          !isCurriculumInMainContent ? (
          <div className="flex flex-col gap-6 [&_h3]:!mb-4">
            <SkillLanguageOverview
              key="skillLanguageOverview"
              associatedSkills={course.associatedSkills}
              prerequisitesHtml={course.prerequisitesHtml}
              syllabusHtml={syllabusHtml}
              formattedLanguage={formattedLanguage}
              formattedTranscripts={formattedTranscripts}
              formattedAiLanguages={formattedAiLanguages}
              isCurriculumInMainContent={isCurriculumInMainContent}
            />
          </div>
        ) : null;
      },
    },
    {
      key: "emphasisModuleVersion",
      renderer: () => {
        return showCourseModes &&
          !isArchived &&
          course?.activeCourseRun?.isEnrollable &&
          emphasisModuleVersion &&
          emphasisModuleVersion !== RedesignedTrackComparison.Hidden ? (
          <div className={maxWidthContainer}>
            {emphasisModuleVersion === RedesignedTrackComparison.Static ? (
              <CertificateEmphasis
                key="certificateEmphasis"
                course={course}
                courseRun={course?.activeCourseRun}
                information={information}
                stats={stats}
                testimonial={testimonial}
                hidePrice={hidePrice}
              />
            ) : (
              <CertificateEmphasisTabs
                key="certificateEmphasisTabs"
                information={information}
                stats={stats}
                testimonial={testimonial}
                pathInfoCoursePrice={coursePrice}
                tabOrder={tabsOrder}
                hidePrice={hidePrice}
                pathInfo={pathInfo}
                course={course}
                ctaElementRenderer={(tabId: string, ctaClassName: string) => {
                  return (
                    <>
                      {coursePrice ? (
                        <CourseEnrollButton
                          buttonClass={ctaClassName}
                          courseRun={course.activeCourseRun!}
                          course={course}
                          location={`cert-module-${tabId}`}
                          price={coursePrice}
                        />
                      ) : (
                        <EnrollLink
                          buttonClass={ctaClassName}
                          courseRun={course.activeCourseRun!}
                          course={course}
                          location={`cert-module-${tabId}`}
                        />
                      )}
                      <AuditLink
                        courseRun={course.activeCourseRun!}
                        course={course}
                        className={ctaClassName}
                        location={`cert-module-${tabId}`}
                      />
                    </>
                  );
                }}
              />
            )}
          </div>
        ) : null;
      },
    },
    {
      key: "highlights",
      renderer: () => {
        return highlights ? (
          <Highlights
            className="bg-putty-300"
            title={highlights.title}
            items={highlights.items}
            partnerName={course.partners?.length ? course.partners[0].name : ""}
            isEnrollable={course.activeCourseRun?.isEnrollable || false}
            showSkillsList={highlights.showSkillsList}
            associatedSkills={course.associatedSkills}
            theme="dark"
            {...(highlights.promo && {
              promo: {
                headline: highlights.promo.headline,
                subtext: highlights.promo.subtext,
                infoText: highlights.promo.infoText,
                headlineMobile: highlights.promo.headlineMobile,
                subtextMobile: highlights.promo.subtextMobile,
                ctaElement: coursePrice ? (
                  <CourseEnrollButton
                    buttonClass="flex-1"
                    courseRun={course.activeCourseRun!}
                    course={course}
                    location="highlights"
                    price={coursePrice}
                  />
                ) : (
                  <EnrollLink
                    buttonClass="flex-1"
                    courseRun={course.activeCourseRun!}
                    course={course}
                    location="highlights"
                  />
                ),
              },
            })}
          />
        ) : null;
      },
    },
    {
      key: "promoBanner",
      renderer: () => {
        return promoBanner && course.activeCourseRun?.isEnrollable ? (
          <OCPromoBanner
            course={course}
            courseRun={course.activeCourseRun}
            data={promoBanner}
          />
        ) : null;
      },
    },
    {
      key: "courseAbout",
      renderer: () => {
        return (
          <>
            <CourseAbout
              key="courseAbout"
              courseData={course}
              fullDescription={productFullDescription}
              video={courseHeaderVideo}
              title={tCourse("courseOverview")}
            />
            <div className="block md:hidden">
              <AccordionText>
                <AccordionTextItem trigger={tCourse("whatYoullLearn")}>
                  <div className="[&_ul]:!px-0 md:[&_ul]:px-4 [&_h2]:hidden">
                    <WhatYoullLearn
                      key="whatYoullLearn"
                      title={tCourse("whatYoullLearn")}
                      parseContent={course.productOverview}
                    />
                  </div>
                  <div className="mt-8">
                    <LanguageOverview
                      key="languageOverview"
                      formattedLanguage={formattedLanguage}
                      formattedTranscripts={formattedTranscripts}
                      formattedAiLanguages={formattedAiLanguages}
                      prerequisitesHtml={course.prerequisitesHtml}
                    />
                  </div>
                </AccordionTextItem>
              </AccordionText>
            </div>
            {course.activeCourseRun?.instructors &&
            course.activeCourseRun?.instructors.length > 0 ? (
              <div className="block md:hidden">
                <AccordionText>
                  <AccordionTextItem trigger={tCourse("instructors")}>
                    {course.activeCourseRun?.instructors &&
                    course.activeCourseRun?.instructors.length > 0 ? (
                      <div className="[&_h2]:hidden">
                        <InstructorList
                          variant="panel"
                          key="instructorList"
                          instructors={
                            course.activeCourseRun?.instructors ?? []
                          }
                        />
                      </div>
                    ) : null}
                  </AccordionTextItem>
                </AccordionText>
              </div>
            ) : null}
          </>
        );
      },
    },
    {
      key: "careerCards",
      renderer: () => {
        const category = getIndustryKey({
          title: course.productName,
          subjectEn: course.subjectEn || "",
          subjectEs: course.subjectEs || "",
          overview: course.productOverview,
          subjects: course.subjects,
          skills: course.skills,
        });
        const careerCardsData =
          CAREER_CARDS_VARIANTS[category || "dataScience"];
        return (
          <CareerCards
            location={careerCards?.location || "detail-page"}
            isSideRail={careerCards?.isSideRail || true}
            industry={careerCards?.industry || category || "dataScience"}
            variant={careerCards?.variantKey || "default"}
            heading={careerCards?.heading || ""}
            title={careerCards?.title || careerCardsData.heading}
            stats={(careerCards?.stats || careerCardsData.cards).slice()}
            skills={resolveCareerCardSkills(
              careerCards?.skills,
              course.skills,
              category || "dataScience",
            ).slice()}
            hiddenCta={true}
          />
        );
      },
    },
    {
      key: "whyLearnersChoose",
      renderer: () => (
        <WhyLearnersChoose
          instructor={
            enrichedInstructors?.[0] ??
            (course.activeCourseRun?.instructors?.[0] as Instructor)
          }
          partnerName={course.partners?.length ? course.partners[0].name : ""}
          location="course-detail"
          enrollmentCount={course.enrollmentCount}
          isArchived={isArchived}
          isEnrollable={course.activeCourseRun?.isEnrollable ?? false}
          highlights={
            [
              highlights?.items?.[0] ?? DEFAULT_HIGHLIGHTS_ITEMS[0],
              highlights?.items?.[1] ?? DEFAULT_HIGHLIGHTS_ITEMS[1],
            ] as HighlightItem[]
          }
        >
          {promoBanner && course.activeCourseRun?.isEnrollable && (
            <OCPromoBanner
              course={course}
              courseRun={course.activeCourseRun}
              data={promoBanner}
            />
          )}
        </WhyLearnersChoose>
      ),
    },
    {
      key: "curriculum",
      renderer: () => {
        return syllabusHtml ? (
          <Curriculum
            key="curriculum"
            syllabus={syllabusHtml}
            weeksToComplete={weeksToComplete}
            minEffort={minEffort}
            maxEffort={maxEffort}
            formattedLanguage={formattedLanguage}
            formattedTranscripts={formattedTranscripts}
            formattedAiLanguages={formattedAiLanguages}
            prerequisitesHtml={course.prerequisitesHtml}
          />
        ) : null;
      },
    },
    {
      key: "benefitsModule",
      renderer: () => {
        return benefitsModule ? (
          <BenefitsModule
            location="detail-page"
            schoolName={titlePartner}
            benefitsModule={benefitsModule}
          />
        ) : null;
      },
    },
    {
      key: "featuredInstructor",
      renderer: () => {
        return enrichedInstructors ? (
          <PartnerInstructors
            key="featuredInstructor"
            instructors={enrichedInstructors}
          />
        ) : null;
      },
    },
    {
      key: "midPageCta",
      renderer: () => {
        return (
          course.activeCourseRun && (
            <div className="[&_h2]:text-base md:[&_h2]:text-3xl">
              <MidPageCTA
                key="midPageCta"
                course={course}
                isArchived={isArchived}
              />
            </div>
          )
        );
      },
    },
    {
      key: "partnershipDescription",
      renderer: () => {
        return course.partners?.length && partnershipDescription ? (
          <PartnershipDescription
            key="partnershipDescription"
            title={partnershipDescription.title}
            prePartnerText={partnershipDescription.prePartnerText}
            postPartnerText={partnershipDescription.postPartnerText}
            partnerLogoList={course.partners?.map(
              (partner: PartialPartner) => partner.logoUrl,
            )}
            partnerNameList={course.partners?.map(
              (partner: PartialPartner) => partner.name,
            )}
            titleClassName="text-2xl md:text-3xl"
          />
        ) : null;
      },
    },
    {
      key: "alumniTestimonials",
      renderer: () => {
        return (
          <AlumniTestimonials
            key="testimonials"
            monarchTestimonials={alumniTestimonials}
            courseTestimonials={course.learnerTestimonials || ""}
            titleClassName="text-center"
          />
        );
      },
    },
    {
      key: "testimonialsModule", //redesign
      renderer: () => {
        return testimonialsModule ? (
          <SocialProof
            header={testimonialsModule.header.title}
            items={testimonialsModule.testimonials}
            linkText={testimonialsModule.header.ctaLinkText}
            linkUrl={testimonialsModule.header.ctaLinkUrl}
            eventConfig={courseDetailPageEventConfig}
            headerTextClassname={headerStylesInDetailPages}
          />
        ) : null;
      },
    },
    {
      key: "instructorList",
      renderer: () => {
        return course.activeCourseRun?.instructors &&
          course.activeCourseRun?.instructors.length > 0 ? (
          <div className="hidden md:block">
            <AccordionText>
              <AccordionTextItem trigger={tCourse("instructors")}>
                <InstructorList
                  variant="panel"
                  instructors={course.activeCourseRun?.instructors ?? []}
                />
              </AccordionTextItem>
            </AccordionText>
          </div>
        ) : null;
      },
    },
    {
      key: "learnerTestimonials",
      renderer: () => {
        return course.learnerTestimonials ? (
          <LearnerTestimonials
            key="learnerTestimonials"
            testimonials={course.learnerTestimonials}
          />
        ) : null;
      },
    },
    {
      key: "faqs",
      renderer: () => {
        return <FAQs key="faqs" course={course} />;
      },
    },
    {
      key: "waysToTakeCourse",
      renderer: () => {
        return showCourseModes &&
          !isArchived &&
          course?.activeCourseRun?.isEnrollable ? (
          showRedesignedTrackComparison ? (
            <div className={maxWidthContainer}>
              <CertificateWaysToTakeCourse
                key="certificateWaysToTakeCourse"
                course={course}
                activeCourseRun={course.activeCourseRun}
                tableDescription={tableDescription}
                hidePrice={hidePrice}
              >
                <div className="flex flex-col md:flex-row gap-2 sm:gap-3 items-center">
                  {coursePrice ? (
                    <CourseEnrollButton
                      buttonClass="flex-1"
                      courseRun={course.activeCourseRun}
                      course={course}
                      location="cert-ways-to-take-course"
                      price={coursePrice}
                    />
                  ) : (
                    <EnrollLink
                      buttonClass="flex-1"
                      courseRun={course.activeCourseRun}
                      course={course}
                      location="cert-ways-to-take-course"
                    />
                  )}

                  <AuditLink
                    courseRun={course.activeCourseRun}
                    course={course}
                    className="flex-1"
                  />
                </div>
              </CertificateWaysToTakeCourse>
            </div>
          ) : (
            <div className={mainContentClass} key="waysToTakeCourse">
              <WaysToTakeCourse
                activeCourseRun={course.activeCourseRun}
                course={course}
              />
            </div>
          )
        ) : null;
      },
    },
    {
      key: "fullProgramCta",
      renderer: () =>
        designToDisplay && ["1", "2", "3"].includes(designToDisplay) ? (
          <RelatedProgram
            designTobeSelected={designToDisplay}
            enrichedPrograms={enrichedPrograms || undefined}
            course={course}
            carouselData={carouselData}
            cardType={cardType}
            expandableSection={expandableSection}
          />
        ) : null,
    },
    {
      key: "statsBanner",
      renderer: () => {
        return statsBanner && statsBanner.items.length > 0 ? (
          <Stats
            stats={statsBanner.items}
            header={statsBanner.header}
            source={statsBanner.source}
            showSliderOnMobile={statsBanner.showSliderOnMobile}
            variant="source"
          />
        ) : null;
      },
    },
    {
      key: "moreAboutCourse",
      renderer: () => {
        return course.additionalInformation ? (
          <MoreAboutCourse
            key="moreAboutCourse"
            additionalInformation={course.additionalInformation}
          />
        ) : null;
      },
    },
    {
      key: "socialShare",
      renderer: () => {
        return (
          <SocialShare
            key="socialShare"
            productUrl={course.productUrl}
            productName={course.productName}
          />
        );
      },
    },
    {
      key: "enterpriseCta",
      renderer: () => {
        return (
          <div
            className="bg-putty-300"
            key="enterpriseCta"
            style={{
              maxWidth: "none",
              width: "100vw",
              marginLeft: "calc(-50vw + 50%)",
            }}
          >
            {!course.frequentlyAskedQuestions && course.hasOfacRestrictions && (
              <OFACMessage type="course" />
            )}
            <EnterpriseCTA productName={course.productName} variant="compact" />
          </div>
        );
      },
    },
    {
      key: "stepComponent",
      renderer: () => {
        return course.activeCourseRun ? (
          <StepComponent
            title={stepComponent?.title}
            steps={stepComponent?.steps}
            programIsEnrollable={course.activeCourseRun?.isEnrollable || false}
            promo={
              stepComponent?.promo
                ? {
                    ...stepComponent.promo,
                    ctaElement: (
                      <div className="flex flex-col md:flex-row gap-2 sm:gap-3 items-center">
                        {coursePrice ? (
                          <CourseEnrollButton
                            buttonClass="flex-1"
                            courseRun={course.activeCourseRun}
                            course={course}
                            location="step-component"
                            price={coursePrice}
                          />
                        ) : (
                          <EnrollLink
                            buttonClass="flex-1"
                            courseRun={course.activeCourseRun}
                            course={course}
                            location="step-component"
                          />
                        )}
                      </div>
                    ),
                  }
                : undefined
            }
            theme={stepComponent?.theme}
            key="contentSteps"
          />
        ) : null;
      },
    },
    {
      key: "verificationUpsellBanner",
      renderer: () => {
        return (
          <VerificationUpsell
            key="verificationUpsellBanner"
            heading={verificationUpsellBanner?.title}
            description={verificationUpsellBanner?.description}
            imageSrc={
              verificationUpsellBanner?.image ||
              "/bg-images/example-certificate-verified.webp"
            }
          />
        );
      },
    },
    {
      key: "languageOverview",
      renderer: () => {
        return (
          <div className="hidden md:block">
            <LanguageOverview
              key="languageOverview"
              formattedLanguage={formattedLanguage}
              formattedTranscripts={formattedTranscripts}
              formattedAiLanguages={formattedAiLanguages}
              prerequisitesHtml={course.prerequisitesHtml}
            />
          </div>
        );
      },
    },
    {
      key: "courseDetails",
      renderer: () => (
        <CourseDetails
          formattedLanguage={formattedLanguage}
          formattedTranscripts={formattedTranscripts}
          formattedAiLanguages={formattedAiLanguages}
          courseRunKey={course.activeCourseRun?.key}
          instructors={course.activeCourseRun?.instructors ?? []}
          course={course}
          courseRun={course.activeCourseRun ?? undefined}
          partnerName={course.partners?.length ? course.partners[0].name : ""}
        />
      ),
    },
    {
      key: "countdownBanner",
      renderer: () => {
        return (
          <FeatureFlagComponentResolverClient
            flagKey="NEXT_PUBLIC_FEATURE_OC_COUNTDOWN_BANNER"
            variants={{
              test: (
                <CountdownBanner
                  variant="cta"
                  theme={countdownBanner?.btf?.theme || "light"}
                  header={countdownBanner?.btf?.text || ""}
                  subHeader={countdownBanner?.btf?.subtext || ""}
                  targetDate={countdownBanner?.btf?.date || ""}
                  htmlId="promo-countdown-banner"
                  location="single-course-detail-page"
                  name="promo-countdown-banner"
                  showSubheader={countdownBanner?.btf?.subtextEnabled ?? false}
                  showCTA={course.activeCourseRun?.isEnrollable ?? false}
                  ctaElement={
                    coursePrice ? (
                      <CourseEnrollButton
                        buttonClass="w-full"
                        courseRun={course.activeCourseRun!}
                        course={course}
                        location="countdown-banner"
                        price={coursePrice}
                      />
                    ) : (
                      <EnrollLink
                        buttonClass="w-full"
                        courseRun={course.activeCourseRun!}
                        course={course}
                        location="countdown-banner"
                      />
                    )
                  }
                />
              ),
              legacy: <></>,
            }}
            fallback="legacy"
          />
        );
      },
    },
    {
      key: "bundleSavingsBanner",
      renderer: () => {
        return bundleSavingsBanner ? (
          <BundleSavingsBanner
            key="bundleSavingsBanner"
            title={bundleSavingsBanner?.title}
            titleEmphasis={bundleSavingsBanner?.titleEmphasis}
            badge={bundleSavingsBanner?.badge}
            description={bundleSavingsBanner?.description}
            discount={bundleSavingsBanner?.discount}
            bulletPoints={bundleSavingsBanner?.bulletPoints}
            cta={{
              label:
                bundleSavingsBanner?.cta?.label ||
                tProgram("exploreRelatedPrograms"),
              url: getProgramSearchUrl(course.partners),
              html_id: "bundling-cta",
              location: "single-course-detail-page",
              name: "single-course-bundling-CTA",
            }}
            partnerName={course.partners?.[0]?.name}
          />
        ) : null;
      },
    },
    {
      key: "productCompare",
      renderer: () => {
        const uuids = [
          course.productUuid,
          ...(productCompare?.uuids || compareFallbackUuids),
        ];
        return productCompare ? (
          <ProductCompare
            productCompare={productCompare}
            productUuids={uuids}
            currentCourseCheckoutUrl={compareCtaUrl}
            location="course-detail-page"
            currentCtaLabel={
              !isArchived && course.activeCourseRun?.isEnrollable
                ? (leadWithPaidData.value?.enrollCtaLabel ??
                  tCourse("advanceYourCareer"))
                : undefined
            }
          />
        ) : null;
      },
    },
  ];

  return (
    <>
      {course.activeCourseRun && (
        <ProductViewTracker
          productId={course.activeCourseRun.key}
          productType="course"
          productName={course.productName}
          productUrl={course.productUrl}
          checkoutUrl={checkoutUrl}
          courseId={course?.productUuid}
          metadata={{
            price: coursePrice || undefined,
            currency: "USD",
            partner: course.partners?.[0]?.key,
            category: course.subjectEn ?? course.subjectEs,
            enrollmentStart: course.activeCourseRun.enrollmentStart?.toString(),
            enrollmentEnd: course.activeCourseRun.enrollmentEnd?.toString(),
            courseRunEnd: course.activeCourseRun.end?.toString(),
          }}
        />
      )}
      <CourseTaggedHeader
        course={course}
        displayPrice={coursePrice}
        currency={pricingData?.currency}
        pricingData={pricingData}
        variant={headerBackground === "light" ? "light" : "dark"}
        priceLabel={headerPriceLabel}
        pricePosition={headerPricePosition}
        simpleMobileHeader={simpleMobileHeader}
        paymentOption={paymentOption}
        showDynamicBadges={showDynamicBadges}
      />

      <CourseCurrencyBarSurface
        course={course}
        pricingData={pricingData}
        courseIsEnrollable={course.activeCourseRun?.isEnrollable ?? false}
        isArchived={isArchived}
        description={
          <>
            You&apos;re enrolling in <strong>{course.productName}</strong> from{" "}
            {titlePartner} in partnership with edX.
          </>
        }
      />

      {course.activeCourseRun && (
        <StickyCTA course={course} courseRun={course.activeCourseRun} />
      )}
      <div
        className="bg-putty-100 flex flex-col !gap-8 md:gap-16 pt-12"
        id="course-decided-details-body-container"
      >
        <div>
          <div className={mainContentClass}>
            {getSortedComponentsBySectionOrder({
              componentList,
              sectionsOrder: mainContent?.map(
                (component) => component.component,
              ),
            })}
          </div>
        </div>
      </div>
    </>
  );
}
